import logoOnLight from '../assets/ipc-global-logo-on-light.png';
import logoOnDark from '../assets/ipc-global-logo-on-dark.svg';

// Official IPC Global logo files, bundled with the app (CSP img-src is
// 'self', so they are never hotlinked):
//   - ipc-global-logo-on-dark.svg: the Brand Guide color-on-dark vector
//     (IPCGlobal-logo-2018-FINAL-ColorOnBlack), the same file the central
//     identity provider uses (ipcgs-cognito-stacks/assets/managed-login).
//   - ipc-global-logo-on-light.png: the color-on-light raster from the same
//     repo (logo-light.png), transparent padding trimmed, pixels unchanged.
// Never replace either with a typeset name or a traced substitute.
export const PRODUCT_NAME = 'onGuard for Entra ID';

/** The official IPC Global logo alone. tone 'light' is for dark backgrounds. */
export function BrandLogo({ tone = 'dark', className = 'h-7 w-auto' }) {
  return (
    <img
      src={tone === 'light' ? logoOnDark : logoOnLight}
      alt="IPC Global"
      className={`${className} flex-shrink-0`}
    />
  );
}

/**
 * The onGuard mark, A2 "Pulse" (a light-blue tiled shield with a heartbeat cut
 * through it; brand/onguard/README.md). Decorative: it always sits beside the
 * visible product name, so alt is empty. tone 'light' is for dark backgrounds
 * (the navy header: white pulse); tone 'dark' is for light backgrounds (IPC
 * dark blue #002F67 pulse). Never a dark pulse on navy.
 * Served from /public (same origin, CSP img-src 'self'), -v5 files.
 */
export const SOLID_MARK_MAX_PX = 20;
export const CRISP_MARK_PX = 32;

export function ProductMark({ tone = 'dark', size = 28, className = '' }) {
  // Size rule (brand/onguard/README.md): the solid pixel drawing below 20 px,
  // the pitch 3 / tile 2 rendering at exactly 32 px (whole-pixel tiles), the
  // gapped vector master everywhere else from 20 px up.
  const light = tone === 'light';
  let src;
  if (size < SOLID_MARK_MAX_PX) src = light ? '/onguard-mark-solid-inverse-v5.svg' : '/onguard-mark-solid-v5.svg';
  else if (size === CRISP_MARK_PX) src = light ? '/onguard-mark-32-inverse-v5.svg' : '/onguard-mark-32-v5.svg';
  else src = light ? '/onguard-mark-inverse-v5.svg' : '/onguard-mark-v5.svg';
  return (
    <img
      src={src}
      alt=""
      height={size}
      style={{ height: size, width: 'auto' }}
      className={`flex-shrink-0 ${className}`}
    />
  );
}

/**
 * IPC Global logo with the onGuard product name beside it.
 *
 * tone 'dark' (default): for light backgrounds (cards, slate-50 pages).
 * tone 'light': for dark backgrounds (the navy header bars).
 * size 'md' for page cards, 'sm' for header bars.
 */
export default function Brand({ tone = 'dark', size = 'md', className = '' }) {
  const onDark = tone === 'light';
  const logoCls = size === 'sm' ? 'h-7' : 'h-10';
  const nameCls = size === 'sm' ? 'text-sm sm:text-base' : 'text-base';
  return (
    <div className={`flex items-center gap-3 min-w-0 ${className}`}>
      <BrandLogo tone={tone} className={`${logoCls} w-auto`} />
      <span
        aria-hidden="true"
        className={`h-7 w-px flex-shrink-0 ${onDark ? 'bg-white/30' : 'bg-slate-300'}`}
      />
      <ProductMark tone={tone} size={28} />
      <span
        className={`${nameCls} font-semibold tracking-tight leading-tight ${onDark ? 'text-white' : 'text-ipc-primary'}`}
      >
        {PRODUCT_NAME}
      </span>
    </div>
  );
}
